import { z } from 'zod';

/**
 * TimeLog access tokens carry identity in the `sub` claim as three
 * colon-separated parts: `siteName:userId:tenantId`.
 *
 * This matches the production parsing in SmartReg's proxy
 * (`SmartReg/server/proxy.cjs`) and Phoenix. Note that
 * `Web/src/auth/testLogin/createTestUser.ts` documents the ordering the other
 * way round in a comment; the runtime behaviour in the proxy is authoritative
 * because it is what the tenant-routing SQL lookup depends on.
 */
export const AuthContextSchema = z.object({
  /** Raw `sub` claim, kept for logging and support. */
  subject: z.string(),
  siteName: z.string(),
  userId: z.string(),
  tenantId: z.string(),
});
export type AuthContext = z.infer<typeof AuthContextSchema>;

export class InvalidSubjectError extends Error {
  constructor(subject: string) {
    super(`Token 'sub' claim is not in the expected siteName:userId:tenantId form: '${subject}'`);
    this.name = 'InvalidSubjectError';
  }
}

/** Parses a `sub` claim into an auth context. Throws InvalidSubjectError when malformed. */
export const parseSubject = (subject: string): AuthContext => {
  const parts = subject.split(':');

  if (parts.length !== 3 || parts.some((part) => part.trim() === '')) {
    throw new InvalidSubjectError(subject);
  }

  const [siteName, userId, tenantId] = parts as [string, string, string];

  return { subject, siteName, userId, tenantId };
};
